import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Swiper from 'swiper';
import { SwiperOptions } from 'swiper/types';

import { LANDING_PAGE_SECTIONS_NAMES } from 'Utilities/analytics';

import { usePageRefEvents } from '../../../../common/Hooks/usePageRefEvents';
import { RoundedSection } from '../../../../common/RoundedSection/RoundedSection';
import { SWIPER_AUTOPLAY } from '../../../../fastlane/swiper/helpers';
import useSwiperCarousel from '../../../../fastlane/swiper/useSwiperCarousel';
import { carPrizeDrawSectionLoadEvent } from '../CarPrizeDraw.helpers';

import Texts from './QuotesSection.texts.json';

import styles from './QuotesSection.module.scss';

const AUTOPLAY_DELAY = 5000;
const AUTOPLAY_SPEED = 800;

export const QuotesSection = () => {
  const [isDomLoaded, setIsDomLoaded] = useState(false);

  const $carousel = useRef<HTMLDivElement | null>(null);
  const $pagination = useRef<HTMLDivElement | null>(null);

  const onRefTrigger = () => carPrizeDrawSectionLoadEvent(LANDING_PAGE_SECTIONS_NAMES.QUOTES);

  const { setRef } = usePageRefEvents({ onRefTrigger });

  useEffect(() => {
    setIsDomLoaded(true);
  }, []);

  const swiperConfig: SwiperOptions = {
    autoplay: {
      delay: AUTOPLAY_DELAY,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    loop: true,
    pagination: {
      clickable: true,
      el: $pagination.current,
    },
    slidesPerView: 1,
    spaceBetween: 16,
    speed: AUTOPLAY_SPEED,
  };

  const swiper = useSwiperCarousel({
    config: {
      $el: $carousel.current,
      type: SWIPER_AUTOPLAY,
      ...swiperConfig,
    },
    enableSwiper: isDomLoaded && Texts.quotes.length > 1,
  }) as Swiper | undefined;

  return (
    <RoundedSection withBottomLedge withTopLedge theme="primary-mid-light-yellow">
      <div ref={(ref) => setRef(ref)} className={styles.component} data-testid="quotesSection">
        <h2 className={styles.title}>
          {Texts.title}
        </h2>
        <div
          ref={(ref) => {
            $carousel.current = ref;
          }}
          className={cx(styles.carousel, 'swiper')}
          data-testid="quotesCarousel"
        >
          <div className={cx(styles.carouselWrapper, 'swiper-wrapper', { [styles.loading]: !swiper })}>
            {Texts.quotes.map(({ attribution, quote }) => (
              <div key={attribution.name} className={cx(styles.slide, 'swiper-slide')}>
                <div className={styles.quoteCard}>
                  <p className={styles.quote}>
                    {quote}
                  </p>
                  <div className={styles.attribution}>
                    <p className={styles.attributionText}>
                      <b>{attribution.name}</b> {attribution.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            ref={(ref) => {
              $pagination.current = ref;
            }}
            className={cx(styles.pagination, 'swiper-pagination')}
          />
        </div>
      </div>
    </RoundedSection>
  );
};
